import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { api } from '../../lib/supabase';
import { useApi } from '../../lib/use-api';
import { useAuth } from '../../lib/auth-context';
import { VerifiedBadge } from '../../components/common/Badges';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Settings, Users, ChevronRight, LogOut, Loader2, Bell, BellOff, UserCog, Save, ExternalLink } from 'lucide-react';
import { formatDuration, formatDistance } from '../../lib/formatters';
import { useUnits, kmToDisplay, unitLabel } from '../../lib/units';
import { countryFlagEmoji } from '../../lib/country';
import { CountryPicker } from '../../components/common/CountryPicker';
import { ORGANIZER_URL } from '../../lib/organizer-url';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  pushSupported,
  pushPermission,
  isSubscribed,
  subscribeToPush,
  unsubscribeFromPush,
} from '../../lib/push';

export default function Profile() {
  const { signOut, profile, user, supabaseEnabled, refreshProfile } = useAuth();
  const nav = useNavigate();
  const { units } = useUnits();
  const { data: runs, loading: runsLoading } = useApi(api.getMyRuns);

  // Auto-scroll to a section when arriving via /profile#recent-runs etc.
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    const id = hash.replace('#', '');
    // Defer one paint so the section has rendered with real data.
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
    return () => clearTimeout(t);
  }, []);

  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const supported = pushSupported();
  const permission = pushPermission();

  // Bio editor
  const [bio, setBio] = useState({
    firstName: profile?.first_name || '',
    lastName: profile?.last_name || '',
    birthday: profile?.birthday || '',
    gender: profile?.gender || '',
    weightKg: profile?.weight_kg ?? '',
    heightCm: profile?.height_cm ?? '',
    country: profile?.country || 'Nigeria',
  });
  const [bioSaving, setBioSaving] = useState(false);
  const [bioOpen, setBioOpen] = useState(false);

  useEffect(() => {
    if (!profile) return;
    setBio({
      firstName: profile.first_name || '',
      lastName: profile.last_name || '',
      birthday: profile.birthday || '',
      gender: profile.gender || '',
      weightKg: profile.weight_kg ?? '',
      heightCm: profile.height_cm ?? '',
      country: profile.country || 'Nigeria',
    });
  }, [profile]);

  async function saveBio() {
    setBioSaving(true);
    try {
      await api.updateMyProfile(bio);
      toast.success('Profile updated');
      refreshProfile?.();
      setBioOpen(false);
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setBioSaving(false);
    }
  }

  useEffect(() => {
    isSubscribed().then(setPushOn);
  }, []);

  async function togglePush(next) {
    setPushBusy(true);
    try {
      if (next) {
        await subscribeToPush();
        setPushOn(true);
        toast.success('Notifications enabled');
      } else {
        await unsubscribeFromPush();
        setPushOn(false);
        toast.success('Notifications disabled');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to update notifications');
    } finally {
      setPushBusy(false);
    }
  }

  async function handleSignOut() {
    if (!supabaseEnabled) {
      toast.info('Sign out requires Supabase to be configured');
      return;
    }
    await signOut();
    toast.success('Signed out');
    nav('/welcome', { replace: true });
  }

  const displayName = profile?.name || user?.email?.split('@')[0] || 'Runner';
  const handle = profile?.handle ? '@' + profile.handle : '';
  const avatar = profile?.avatar_url || 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200';
  const totalRuns = profile?.total_runs ?? 0;
  const totalKm = Number(profile?.total_distance_km ?? 0);
  const verifiedFinishes = profile?.verified_finishes ?? 0;
  const bestPaceSec = profile?.best_pace_sec;
  const bestPaceConverted = bestPaceSec ? Math.round(units === 'mi' ? bestPaceSec * 1.609344 : bestPaceSec) : null;
  const bestPace = bestPaceConverted
    ? `${Math.floor(bestPaceConverted / 60)}:${String(bestPaceConverted % 60).padStart(2, '0')}/${unitLabel(units)}`
    : '—';

  return (
    <div className="px-4 pt-4 pb-6 space-y-5" data-testid="profile-screen">
      <div className="flex items-center justify-between">
        <p className="rivl-label">Profile</p>
        <Button variant="ghost" size="icon" data-testid="profile-settings" onClick={() => nav('/settings')}>
          <Settings className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <div className="h-20 w-20 rounded-md bg-cover bg-center border border-border" style={{ backgroundImage: `url(${avatar})` }} />
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-bold tracking-tight truncate flex items-center gap-2" data-testid="profile-name">
            {displayName}
            {profile?.country && (
              <span className="text-2xl leading-none" data-testid="profile-country-flag" title={profile.country} aria-label={profile.country}>
                {countryFlagEmoji(profile.country)}
              </span>
            )}
          </h1>
          {handle && <p className="text-sm text-muted-foreground truncate">{handle}</p>}
          {user?.email && <p className="text-[11px] text-muted-foreground font-mono truncate mt-0.5">{user.email}</p>}
          <div className="mt-2 flex items-center gap-2">
            <VerifiedBadge />
            <span className="text-[11px] text-muted-foreground">{verifiedFinishes} verified finishes</span>
          </div>
        </div>
      </div>

      <div id="stats" className="grid grid-cols-3 gap-3">
        <Stat label="Runs" value={totalRuns} />
        <Stat label="Distance" value={`${kmToDisplay(totalKm, units).toFixed(0)}${unitLabel(units)}`} />
        <Stat label="Best pace" value={bestPace} />
      </div>

      <div className="rivl-card" data-testid="profile-bio-card">
        <button
          onClick={() => setBioOpen((v) => !v)}
          className="w-full p-4 flex items-center gap-3 hover:bg-secondary/30 rounded-md transition-colors text-left"
          data-testid="profile-bio-toggle"
        >
          <div className="h-10 w-10 rounded-md bg-secondary flex items-center justify-center">
            <UserCog className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="font-display font-semibold">Bio data</div>
            <div className="text-xs text-muted-foreground">
              {bio.firstName || bio.lastName || bio.birthday || bio.gender || bio.weightKg || bio.heightCm || bio.country !== 'Nigeria'
                ? `${[bio.firstName, bio.lastName].filter(Boolean).join(' ') || '—'}${bio.birthday ? ` · ${ageFromBirthday(bio.birthday)}y` : ''} · ${bio.gender || '—'} · ${bio.country || '—'}`
                : 'Add name, birthday, gender, country'}
            </div>
          </div>
          <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${bioOpen ? 'rotate-90' : ''}`} />
        </button>

        {bioOpen && (
          <div className="px-4 pb-4 pt-1 space-y-3 border-t border-border">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="rivl-label text-[10px]">First name</Label>
                <Input
                  data-testid="bio-first-name"
                  value={bio.firstName}
                  onChange={(e) => setBio((b) => ({ ...b, firstName: e.target.value }))}
                  placeholder="Alex"
                  className="h-11"
                />
              </div>
              <div>
                <Label className="rivl-label text-[10px]">Last name</Label>
                <Input
                  data-testid="bio-last-name"
                  value={bio.lastName}
                  onChange={(e) => setBio((b) => ({ ...b, lastName: e.target.value }))}
                  placeholder="Mercer"
                  className="h-11"
                />
              </div>
            </div>

            <div>
              <Label className="rivl-label text-[10px]">Birthday</Label>
              <Input
                data-testid="bio-birthday"
                type="date"
                value={bio.birthday}
                onChange={(e) => setBio((b) => ({ ...b, birthday: e.target.value }))}
                max={new Date().toISOString().slice(0, 10)}
                className="h-11"
              />
              {bio.birthday && ageFromBirthday(bio.birthday) != null && (
                <p className="text-[11px] text-muted-foreground mt-1" data-testid="bio-age-readout">
                  Age: {ageFromBirthday(bio.birthday)}
                </p>
              )}
            </div>

            <div>
              <Label className="rivl-label text-[10px]">Gender</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {['male', 'female', 'other'].map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setBio((b) => ({ ...b, gender: g }))}
                    data-testid={`bio-gender-${g}`}
                    className={`h-10 rounded-md border text-xs uppercase tracking-wide font-medium ${
                      bio.gender === g ? 'border-primary bg-primary/5 text-primary' : 'border-border'
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="rivl-label text-[10px]">Weight ({units === 'mi' ? 'lb' : 'kg'})</Label>
                <Input
                  data-testid="bio-weight"
                  type="number"
                  inputMode="decimal"
                  value={units === 'mi' ? (bio.weightKg ? (Number(bio.weightKg) * 2.20462).toFixed(0) : '') : bio.weightKg}
                  onChange={(e) => {
                    const v = e.target.value;
                    setBio((b) => ({ ...b, weightKg: v === '' ? '' : (units === 'mi' ? (Number(v) / 2.20462).toFixed(1) : v) }));
                  }}
                  placeholder={units === 'mi' ? 'e.g. 159' : 'e.g. 72'}
                  className="h-11 font-mono"
                />
              </div>
              <div>
                <Label className="rivl-label text-[10px]">Height ({units === 'mi' ? 'in' : 'cm'})</Label>
                <Input
                  data-testid="bio-height"
                  type="number"
                  inputMode="numeric"
                  value={units === 'mi' ? (bio.heightCm ? (Number(bio.heightCm) / 2.54).toFixed(0) : '') : bio.heightCm}
                  onChange={(e) => {
                    const v = e.target.value;
                    setBio((b) => ({ ...b, heightCm: v === '' ? '' : (units === 'mi' ? (Number(v) * 2.54).toFixed(1) : v) }));
                  }}
                  placeholder={units === 'mi' ? 'e.g. 70' : 'e.g. 178'}
                  className="h-11 font-mono"
                />
              </div>
            </div>

            <div>
              <Label className="rivl-label text-[10px]">Country</Label>
              <CountryPicker
                value={bio.country}
                onChange={(v) => setBio((b) => ({ ...b, country: v }))}
              />
            </div>

            <Button
              onClick={saveBio}
              disabled={bioSaving || !supabaseEnabled}
              className="w-full h-11 rounded-md"
              data-testid="bio-save"
            >
              {bioSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Save bio</>}
            </Button>
            {!supabaseEnabled && (
              <p className="text-[11px] text-muted-foreground text-center">Sign in to save changes.</p>
            )}
          </div>
        )}
      </div>

      <Link to="/friends" className="rivl-card p-4 flex items-center gap-3 hover:border-primary transition-colors" data-testid="profile-friends-link">
        <div className="h-10 w-10 rounded-md bg-secondary flex items-center justify-center">
          <Users className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="font-display font-semibold">Friends</div>
          <div className="text-xs text-muted-foreground">Manage your network</div>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </Link>

      <div className="rivl-card p-4 flex items-center gap-3" data-testid="profile-push-toggle">
        <div className={`h-10 w-10 rounded-md flex items-center justify-center ${pushOn ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}>
          {pushOn ? <Bell className="h-5 w-5" /> : <BellOff className="h-5 w-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold">Race-day notifications</div>
          <div className="text-xs text-muted-foreground">
            {!supported
              ? 'Not supported on this browser'
              : permission === 'denied'
                ? 'Blocked — change in browser settings'
                : pushOn
                  ? 'Check-in opens, race start, verified results'
                  : 'Off — turn on to get race-day alerts'}
          </div>
        </div>
        {pushBusy ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          <Switch
            checked={pushOn}
            disabled={!supported || permission === 'denied' || !supabaseEnabled}
            onCheckedChange={togglePush}
            data-testid="push-switch"
          />
        )}
      </div>

      <div id="recent-runs">
        <h2 className="rivl-label mb-3">Recent personal runs</h2>
        {runsLoading ? (
          <div className="py-6 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
        ) : runs?.length > 0 ? (
          <div className="rivl-card divide-y divide-border" data-testid="personal-runs-list">
            {runs.map((r) => (
              <Link
                key={r.id}
                to={`/runs/${r.id}`}
                className="px-4 py-3 flex items-center gap-3 hover:bg-secondary/30 transition-colors"
                data-testid={`personal-run-row-${r.id}`}
              >
                <div className="flex-1">
                  <div className="font-mono font-semibold">{formatDistance(kmToDisplay(r.distanceKm, units))} {unitLabel(units)}</div>
                  <div className="text-[11px] text-muted-foreground">{r.date} · {r.visibility}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-bold">{formatDuration(r.durationSec)}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">{r.paceMinPerKm}/{unitLabel(units)}</div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-6 text-center">No personal runs yet. Start one from the Create tab.</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        {(profile?.role === 'organizer' || profile?.role === 'admin') && (
          <Button
            asChild
            variant="outline"
            className="h-11 rounded-md"
            data-testid="profile-organizer"
          >
            <a href={ORGANIZER_URL} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4 mr-2" /> Organizer
            </a>
          </Button>
        )}
        <Button
          variant="outline"
          onClick={handleSignOut}
          className={`h-11 rounded-md text-destructive border-destructive/40 hover:bg-destructive hover:text-destructive-foreground ${profile?.role === 'organizer' || profile?.role === 'admin' ? '' : 'col-span-2'}`}
          data-testid="profile-signout"
        >
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </Button>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rivl-card py-3 text-center">
      <div className="font-mono font-bold text-2xl">{value}</div>
      <div className="rivl-label text-[9px]">{label}</div>
    </div>
  );
}


function ageFromBirthday(yyyy_mm_dd) {
  if (!yyyy_mm_dd) return null;
  const dob = new Date(yyyy_mm_dd);
  if (Number.isNaN(dob.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
  if (age < 0 || age > 130) return null;
  return age;
}
